// Issue #143 sub-step 6: react-router v6 migration.
// v5 `useHistory().push(path)` -> v6 `useNavigate()(path)`.
import { useContext, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { DataContext } from '../DataContextProvider';
import { ApplicantInfo, searchableFields } from '../types';
import { naWrapper } from '../utils';


const SearchBoxContainer = styled(Box)(({ theme: { palette, shape, spacing } }) => ({
    display: 'flex',
    alignContent: 'center',
    borderRadius: shape.borderRadius,
    padding: spacing(0.25, 2, 0.25, 0),
    backgroundColor: alpha(palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(palette.common.white, 0.25)
    }
}));

const IconWrapper = styled(Box)(({ theme: { spacing } }) => ({
    padding: spacing(0, 2),
    display: 'grid',
    placeContent: 'center'
}));

const hiraganaToKanakana = (str: string) => str.replace(/[\u3041-\u3096]/g, match => String.fromCharCode(match.charCodeAt(0) + 0x60));

const matchApplicant = (applicant: ApplicantInfo, inputValue: string) => {
    const regExp = new RegExp(`${inputValue}|${hiraganaToKanakana(inputValue)}`, 'i');
    for (let field of searchableFields) {
        const fieldValue = naWrapper((field === 'schoolChoice' ? applicant[field]['others'] : applicant[field]) as string);
        if (fieldValue) {
            const match = fieldValue.search(regExp);
            if (match > -1) {
                return { field, matchIndex: match };
            }
        }
    }
    return null;
};

const SearchBox = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { state: { loading, data } } = useContext(DataContext);

    return (
        <SearchBoxContainer>
            <IconWrapper>
                <SearchIcon />
            </IconWrapper>
            <Autocomplete
                freeSolo
                loading={loading}
                options={data}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                getOptionLabel={option => (option as ApplicantInfo).name}
                renderOption={(props, option, { inputValue }) => {
                    const match = matchApplicant(option, inputValue);
                    if (!match) return;
                    const { field, matchIndex } = match;
                    const value = naWrapper((field === 'schoolChoice' ? option[field]['others'] : option[field]) as string);

                    const clipThreshold = 30;

                    const start = Math.max(0, matchIndex - clipThreshold);
                    const end = Math.min(value.length, matchIndex + inputValue.length + clipThreshold)

                    const secondaryText = field === 'name' ? <Fragment /> : (
                        <Fragment>
                            {start > 0 && '...'}{value.slice(start, matchIndex)}
                            <b>{inputValue}</b>
                            {value.slice(matchIndex + inputValue.length, end)}{end < value.length && '...'}
                        </Fragment>
                    );

                    return (
                        <li {...props} onClick={() => {
                            navigate(`/${option.applicantNum}`);
                            setOpen(false);
                        }}>
                            <ListItemText primary={option.name} secondary={secondaryText} />
                        </li>
                    )
                }}
                filterOptions={(options, { inputValue }) => options.filter(option => (
                    matchApplicant(option, inputValue)
                ))}
                renderInput={(params) => {
                    const { InputLabelProps, InputProps, ...rest } = params;
                    return (
                        <InputBase
                            {...InputProps}
                            {...rest}
                            placeholder="検索"
                            fullWidth
                            style={{ flex: 1, width: '240px', color: 'inherit' }}
                        />
                    )
                }}
            />
        </SearchBoxContainer>
    );
};

export default SearchBox;
